/**
 * Audit Welle C — CoinGecko-Krypto-Fundamentals.
 *
 * Liest die per Cron (refresh-coingecko.mts) gepflegten Daten aus Supabase:
 *   - Marketcap, ATH, ATH-Distance, Days-since-ATH, Volume_24h
 *   - BTC-Dominanz (global)
 *
 * Lese-Pfad ist Anon-zugänglich (RLS-Policy cf_anyone_select / cmg_anyone_select),
 * damit auch die Demo die Daten sehen kann.
 */

import { supabase } from './supabase';

export interface CryptoFundamentals {
  symbol: string;
  coingecko_id: string;
  name: string | null;
  current_price: number | null;
  market_cap_usd: number | null;
  market_cap_rank: number | null;
  total_volume_24h_usd: number | null;
  ath_usd: number | null;
  ath_date: string | null;
  ath_change_pct: number | null;
  atl_usd: number | null;
  atl_date: string | null;
  atl_change_pct: number | null;
  circulating_supply: number | null;
  fetched_at: string;
}

export interface CryptoMarketGlobal {
  btc_dominance_pct: number | null;
  eth_dominance_pct: number | null;
  total_market_cap_usd: number | null;
  total_volume_24h_usd: number | null;
  active_cryptocurrencies: number | null;
  fetched_at: string;
}

export async function fetchCryptoFundamentals(
  symbol: string,
  signal: AbortSignal
): Promise<CryptoFundamentals | null> {
  const { data, error } = await supabase
    .from('v5_crypto_fundamentals')
    .select('*')
    .eq('symbol', symbol.toUpperCase())
    .abortSignal(signal)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as CryptoFundamentals | null;
}

export async function fetchCryptoMarketGlobal(
  signal: AbortSignal
): Promise<CryptoMarketGlobal | null> {
  const { data, error } = await supabase
    .from('v5_crypto_market_global')
    .select('*')
    .eq('id', 1)
    .abortSignal(signal)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as CryptoMarketGlobal | null;
}

/**
 * Heuristik: Akkumulationszone-Score (0-100).
 * Liefert eine Einschätzung in Klartext: was bedeutet die aktuelle Phase und was tut man jetzt?
 */
export interface BearPhaseAssessment {
  /** 0-100 — wie attraktiv ist die Phase für Käufer (höher = besser) */
  score: number;
  /** Phasen-Name in Deutsch, kein Trader-Slang */
  phase: string;
  /** Kurze Faktenzeile (z. B. „53 % unter Höchststand, 287 Tage her") */
  facts: string;
  /** Mehrzeilige Klartext-Erklärung: WAS PASSIERT GERADE und WAS BEDEUTET DAS */
  meaning: string;
  /** Konkrete Handlungs-Empfehlung */
  action: string;
}

export function bearMarketAccumulationScore(
  fund: CryptoFundamentals | null
): BearPhaseAssessment | null {
  if (!fund || fund.ath_change_pct === null || !fund.ath_date) return null;

  const drawdown = Math.abs(fund.ath_change_pct);
  const daysSinceAth = Math.round(
    (Date.now() - Date.parse(fund.ath_date)) / (1000 * 60 * 60 * 24)
  );

  let score: number;
  let phase: string;
  let meaning: string;
  let action: string;

  if (drawdown >= 85) {
    score = 95;
    phase = 'Kapitulation (extremer Bärenmarkt)';
    meaning =
      'Der Kurs hat etwa 85 % oder mehr vom Höchststand verloren. Das ist der seltene Bereich, in dem auch Langzeit-Halter aufgeben („Kapitulation"). Historisch war das bei Bitcoin der Boden in den Bärenmärkten 2015 und 2018/19.';
    action =
      '✓ Klassische Lehrbuch-Einstiegszone. Position vorsichtig in mehreren Tranchen aufbauen. Das Tief kann noch tiefer gehen, aber das Chance-Risiko-Verhältnis ist auf Sicht 1–2 Jahre meist ausgezeichnet.';
  } else if (drawdown >= 70) {
    score = 80;
    phase = 'Tiefer Bärenmarkt';
    meaning =
      'Der Kurs hat 70 % oder mehr vom Höchststand verloren. Klassische späte Bärenmarkt-Phase — Stimmung ist mies, Schlagzeilen negativ, viele Anleger haben aufgegeben. In genau dieser Stimmung beginnen historisch die nächsten Aufwärtszyklen.';
    action =
      '✓ Gute Akkumulationszone für langfristigen Aufbau. Erste Käufe sinnvoll, Stops weit setzen oder ohne Stop arbeiten (langer Horizont). Nicht alles auf einmal kaufen.';
  } else if (drawdown >= 50) {
    score = 60;
    phase = 'Mittlerer Bärenmarkt';
    meaning =
      'Der Kurs hat sich vom Höchststand etwa halbiert. Bärenmarkt ist klar etabliert, aber das Tief ist statistisch noch nicht garantiert — historisch haben Krypto-Bärenmärkte oft erst bei −70 bis −85 % gedreht.';
    action =
      '⚠ Beobachten und kleine Erst-Positionen denkbar, aber keine großen Käufe. Wenn der Kurs weiter fällt, ist mehr Pulver für die wirklich attraktive Zone (−70 % +) wertvoller.';
  } else if (drawdown >= 30) {
    score = 35;
    phase = 'Korrektur im Trend';
    meaning =
      'Der Kurs ist 30–50 % vom Höchststand entfernt. Das ist noch keine Bärenmarkt-Diagnose — bei Krypto sind Pullbacks dieser Größenordnung normal innerhalb eines intakten Aufwärtstrends.';
    action =
      '⚠ Geduld. Entweder ist das eine Pullback-Kaufgelegenheit innerhalb des Trends, oder der Beginn eines tieferen Bärenmarktes — aktuell unklar. Auf Trendbestätigung warten (z. B. Wochen-Schlusskurs zurück über MA200).';
  } else {
    score = 10;
    phase = 'Nahe Höchststand';
    meaning =
      'Der Kurs ist weniger als 30 % vom Allzeithoch entfernt. Kein Bärenmarkt — eher der „normale" Aufwärtstrend-Bereich.';
    action =
      '✗ Kein Akkumulations-Setup. Wer hier kauft, kauft nahe dem Hoch — typisches Falle-Niveau. Auf Pullback warten.';
  }

  // Verstärken wenn Bärenmarkt schon lange läuft (lange Tiefstands-Phasen sind historisch besser für Akkumulation)
  if (daysSinceAth >= 365 && drawdown >= 50) score = Math.min(100, score + 5);
  if (daysSinceAth >= 730 && drawdown >= 50) score = Math.min(100, score + 5);

  const facts = `${drawdown.toFixed(1)} % unter dem Allzeithoch, ${daysSinceAth} Tage her`;

  return { score, phase, facts, meaning, action };
}
